/**
 * Rooftop Page Manager
 * 2 sections: Rooftop Experience + Rooftop Gallery
 */

import { useState, useEffect } from 'react';
import { Building2, Info, Image as ImageIcon, History, Clock, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '../../components/ui/button';
import { InlineText, InlineArray, InlineImage, SectionCard as InlineSectionCard } from '../../components/cms/InlineEditors';
import RichTextEditor from '../../components/cms/RichTextEditor';
import { BatchImageUpload } from '../../components/cms/BatchImageUpload';
import { VersionHistoryViewer } from '../../components/cms/VersionHistoryViewer';
import versionHistoryService from '../../services/versionHistoryService';
import cmsApiService, { type RooftopItemDTO, type RooftopSectionDTO } from '../../services/cmsApiService';

interface FeatureItem {
  [key: string]: unknown;
  id?: number;
  name: string;
  subname: string;
  imageUrl: string;
  _isNew?: boolean;
  _isEdited?: boolean;
}

interface GalleryImage {
  [key: string]: unknown;
  id?: number;
  name: string;
  subName: string;
  imageUrl: string;
  _isNew?: boolean;
  _isEdited?: boolean;
}

interface PageContentSection {
  id?: number;
  pageName: string;
  sectionName: string;
  attributes: Record<string, unknown>;
}

export default function NewRooftopPageManager() {
  const [historyDialog, setHistoryDialog] = useState<{ type: string; id: number } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [rooftopId, setRooftopId] = useState<number | null>(null);
  const [galleryId, setGalleryId] = useState<number | null>(null);

  const [experienceSection, setExperienceSection] = useState<PageContentSection>({
    pageName: 'ROOFTOP',
    sectionName: 'experience',
    attributes: {
      title: 'Grand Pittu Skylounge Experience',
      description: 'Enjoy breathtaking city views, curated ambience...',
      callButtonText: 'Call Us',
      features: [] as FeatureItem[],
    },
  });

  const [gallerySection, setGallerySection] = useState({
    subtitle: 'Rooftop Views',
    title: 'Our Rooftop Gallery',
    images: [] as GalleryImage[],
  });

  // Load data from API
  useEffect(() => {
    loadRooftopData();
  }, []);

  const loadRooftopData = async () => {
    setIsLoading(true);
    try {
      // Load rooftop experience section
      const rooftopResponse = await cmsApiService.getRooftopSectionByPage('ROOFTOP');
      
      if (rooftopResponse) {
        setRooftopId(rooftopResponse.id);
        
        // Map features (items array)
        const mappedFeatures = (rooftopResponse.items || []).map((item: any) => ({
          id: item.id,
          name: item.name || '',
          subname: item.subName || '',
          imageUrl: item.imageUrl || '',
        })) as FeatureItem[];

        setExperienceSection(prev => ({
          ...prev,
          id: rooftopResponse.id,
          attributes: {
            ...prev.attributes,
            title: rooftopResponse.title || 'Grand Pittu Skylounge Experience',
            description: rooftopResponse.description || '',
            callButtonText: rooftopResponse.buttonName || 'Call Us',
            features: mappedFeatures,
          },
        }));
      }

      // Load gallery section using /gallery/by-name/ROOFTOP
      try {
        const rooftopGallery = await cmsApiService.getGallerySectionByName('ROOFTOP');
        
        if (rooftopGallery) {
          setGalleryId(rooftopGallery.id);
          
          const mappedGalleryImages = (rooftopGallery.images || []).map((img: any) => ({
            id: img.id,
            name: img.name || '',
            subName: img.subName || '',
            imageUrl: img.imageUrl || '',
          })) as GalleryImage[];

          setGallerySection({
            subtitle: rooftopGallery.subtitle || 'Rooftop Views',
            title: rooftopGallery.title || 'Rooftop Gallery',
            images: mappedGalleryImages,
          });
        } else {
          // Set default values - will be created on first save
          setGallerySection({
            subtitle: 'Rooftop Views',
            title: 'Rooftop Gallery',
            images: [],
          });
        }
      } catch (galleryError) {
        // No rooftop gallery found - set defaults
        setGallerySection({
          subtitle: 'Rooftop Views',
          title: 'Rooftop Gallery',
          images: [],
        });
      }
      
      toast.success('Rooftop sections loaded successfully');
    } catch (error) {
      toast.error('Failed to load rooftop sections');
    } finally {
      setIsLoading(false);
    }
  };

  const markChanged = () => setHasUnsavedChanges(true);

  // Save experience features: new features via add-images, edited features via features/{id}
  const saveFeatures = async (): Promise<boolean> => {
    if (!rooftopId) {
      return true;
    }

    const currentFeatures = (experienceSection.attributes.features as FeatureItem[]) || [];
    
    const newFeatures = currentFeatures.filter(f => !f.id || f._isNew);
    const editedFeatures = currentFeatures.filter(f => f.id && f._isEdited && !f._isNew);

    try {
      if (newFeatures.length > 0) {
        const addItemsDto = newFeatures.map(f => ({
          name: f.name,
          subName: f.subname,
          imageUrl: f.imageUrl
        }));
        
        await cmsApiService.addRooftopItems(rooftopId, addItemsDto);
        toast.success(`Added ${newFeatures.length} new feature(s)`);
      }

      for (const feature of editedFeatures) {
        const updateDto: RooftopItemDTO = {
          id: feature.id,
          name: feature.name,
          subName: feature.subname,
          imageUrl: feature.imageUrl
        };
        
        await cmsApiService.updateRooftopImageDetails(feature.id!, updateDto);
      }
      
      if (editedFeatures.length > 0) {
        toast.success(`Updated ${editedFeatures.length} feature(s)`);
      }

      return true;
    } catch (error) {
      toast.error('Failed to save features');
      return false;
    }
  };

  // Save gallery images using gallery-controller
  const saveGalleryImages = async (): Promise<boolean> => {
    if (!galleryId) {
      return true;
    }

    const currentImages = gallerySection.images || [];
    const newImages = currentImages.filter(img => !img.id || img._isNew);

    try {
      if (newImages.length > 0) {
        const addImagesDto = newImages.map(img => ({
          name: img.name || '',
          subName: img.subName || '',
          imageUrl: img.imageUrl
        }));
        
        await cmsApiService.addGalleryImages(galleryId, addImagesDto);
        toast.success(`Added ${newImages.length} new gallery image(s)`);
        await loadRooftopData(); // Reload to get IDs
      }

      return true;
    } catch (error) {
      toast.error('Failed to save gallery images');
      return false;
    }
  };

  const saveAllSections = async () => {
    setIsSaving(true);
    
    try {
      // Rooftop experience section DTO (no gallery fields)
      const rooftopDto: Partial<RooftopSectionDTO> = {
        pageName: 'ROOFTOP',
        title: experienceSection.attributes.title as string,
        subTitle: '',
        description: experienceSection.attributes.description as string,
        buttonName: experienceSection.attributes.callButtonText as string,
        imgUrl: '',
        contactSubTitle: '',
        contactTitle: '',
        ctaTitle: '',
        ctaSubTitle: '',
        ctaButtonText: '',
        ctaImgUrl: '',
        ctaDescription: '',
      };
      
      // Gallery section DTO (separate from rooftop)
      const galleryDto = {
        title: gallerySection.title,
        subtitle: gallerySection.subtitle,
        imageUrl: '',
        buttonText: '',
      };

     
      let sectionId = rooftopId;
      
      // Save rooftop experience section
      if (rooftopId) {
        const response = await cmsApiService.updateRooftopSection(rooftopId, rooftopDto);
        await saveFeatures();
        toast.success('Rooftop experience section updated');
      } else {
        const createRooftopDto: RooftopSectionDTO = {
          ...rooftopDto,
          pageName: 'ROOFTOP',
          items: (experienceSection.attributes.features as FeatureItem[]).map(f => ({
            name: f.name,
            subName: f.subname,
            imageUrl: f.imageUrl
          })),
        } as RooftopSectionDTO;
        
        const response = await cmsApiService.addRooftopSection(createRooftopDto);
        if (response?.id) {
          setRooftopId(response.id);
          sectionId = response.id;
        }
        toast.success('Rooftop experience section created');
      }
      
      // Save gallery section separately
      if (galleryId) {
        await cmsApiService.updateGallerySection(galleryId, galleryDto);
        await saveGalleryImages();
        toast.success('Gallery section updated');
      } else {
        const createGalleryDto = {
          title: 'Rooftop Gallery',
          subtitle: gallerySection.subtitle || 'Rooftop Views',
          imageUrl: '',
          buttonText: '',
          sectionName: 'ROOFTOP', // Use sectionName to identify rooftop gallery
          images: gallerySection.images.map(img => ({
            name: img.name || '',
            subName: img.subName || '',
            imageUrl: img.imageUrl
          })),
        };
        
        const galleryResponse = await cmsApiService.addGallerySection(createGalleryDto);
        if (galleryResponse?.id) {
          setGalleryId(galleryResponse.id);
        }
        toast.success('Rooftop gallery section created');
      }

      versionHistoryService.track({
        entityType: 'rooftop-section',
        entityId: sectionId || 0,
        action: rooftopId ? 'update' : 'create',
        changes: { rooftop: { old: {}, new: rooftopDto } },
        snapshot: rooftopDto as Record<string, unknown>,
        user: 'Admin',
      });

      setHasUnsavedChanges(false);
      await loadRooftopData();
      
    } catch (error) {
      toast.error('Failed to save rooftop section');
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageUpload = async (file: File): Promise<string> => {
    try {
      const response = await cmsApiService.uploadImage(file, 'rooftop');
      return response.url || response.imageUrl || response;
    } catch (error) {
      toast.error('Failed to upload image');
      throw error;
    }
  };

  const handleDeleteFeature = async (item: FeatureItem, index: number): Promise<boolean> => {
    if (!item.id) {
      // If no ID, it's a new unsaved item, just return true to remove from UI
      return true;
    }
    
    try {
      await cmsApiService.deleteRooftopImageDetails(item.id);
      toast.success('Feature deleted successfully');
      return true;
    } catch (error) {
      toast.error('Failed to delete feature');
      return false;
    }
  };

  // Handler for feature changes - marks new items as _isNew and edited items as _isEdited
  const handleFeaturesChange = (items: FeatureItem[]) => {
    const currentFeatures = (experienceSection.attributes.features as FeatureItem[]) || [];
    
    const processedItems = items.map(item => {
      if (!item.id) {
        return { ...item, _isNew: true };
      }
      
      const existingItem = currentFeatures.find(f => f.id === item.id);
      if (existingItem) {
        const isEdited = existingItem.name !== item.name || 
                         existingItem.subname !== item.subname || 
                         existingItem.imageUrl !== item.imageUrl;
        if (isEdited) {
          return { ...item, _isEdited: true };
        }
      }
      
      return item;
    });
    
    setExperienceSection((p) => ({
      ...p,
      attributes: { ...p.attributes, features: processedItems }
    }));
    markChanged();
  };

  // Handler for gallery images change - DO NOT auto-save, wait for manual save
  const handleGalleryImagesChange = (newImages: Array<{ imageUrl: string; id?: number }>) => {
    const updatedImages = newImages.map((img: { imageUrl: string; id?: number }) => ({
      ...img,
      name: '',
      subName: '',
      _isNew: !img.id,
    })) as GalleryImage[];
    
    setGallerySection(prev => ({ ...prev, images: updatedImages }));
    markChanged(); // Just mark as changed, don't auto-save
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Clock className="w-8 h-8 animate-spin mx-auto mb-2" />
          <p>Loading rooftop section...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between backdrop-blur-sm bg-white/80 dark:bg-slate-900/80 rounded-2xl p-6 border-2 border-slate-200 dark:border-slate-800 shadow-sm">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 dark:from-white dark:via-slate-100 dark:to-slate-300 bg-clip-text text-transparent">
            <Building2 className="w-8 h-8" />
            Rooftop Page Manager
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadRooftopData} disabled={isLoading || isSaving}>
            <History className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Unsaved Changes Warning Banner */}
      {hasUnsavedChanges && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
          <div className="flex items-center justify-between">
            <div className="flex">
              <Clock className="w-5 h-5 text-amber-400 mr-2" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  You have unsaved changes
                </p>
                <p className="text-xs text-amber-600">
                  Click Save All to persist your changes.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setHasUnsavedChanges(false);
                  loadRooftopData();
                }}
                className="text-amber-800 border-amber-300 hover:bg-amber-100"
              >
                Discard
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={saveAllSections}
                disabled={isSaving}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {isSaving ? 'Saving...' : 'Save Now'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Section 1: Rooftop Experience */}
      <InlineSectionCard
        title="Section 1: Rooftop Experience"
        description="Main content with features"
        icon={<Info className="w-5 h-5" />}
        onViewHistory={() => setHistoryDialog({ type: 'rooftop-section', id: rooftopId || 1 })}
      >
        <InlineText
          label="Title"
          value={(experienceSection.attributes.title as string) || ''}
          onChange={(v) => {
            setExperienceSection((p) => ({ ...p, attributes: { ...p.attributes, title: v } }));
            markChanged();
          }}
        />
        <div className="space-y-2">
          <div className="text-sm font-medium">Description</div>
          <RichTextEditor
            value={(experienceSection.attributes.description as string) || ''}
            onChange={(v) => {
              setExperienceSection((p) => ({ ...p, attributes: { ...p.attributes, description: v } }));
              markChanged();
            }}
            placeholder="Write rooftop experience description..."
            minHeight="160px"
          />
        </div>
        <InlineText
          label="Call Button Text"
          value={(experienceSection.attributes.callButtonText as string) || ''}
          onChange={(v) => {
            setExperienceSection((p) => ({ ...p, attributes: { ...p.attributes, callButtonText: v } }));
            markChanged();
          }}
        />

        <div className="space-y-2">
          <InlineArray
            items={experienceSection.attributes.features as FeatureItem[]}
            onChange={handleFeaturesChange}
            onDeleteItem={handleDeleteFeature}
            label="Features"
            getDefaultItem={() => ({ name: '', subname: '', imageUrl: '', _isNew: true })}
            canAdd={true}
            canDelete={true}
            canReorder={true}
            renderItem={(item) => (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded overflow-hidden">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center">
                      <ImageIcon className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div>
                  <div className="font-medium">{item.name}</div>
                  <div className="text-sm text-muted-foreground">{item.subname}</div>
                </div>
              </div>
            )}
            renderForm={(item, onChange, onSave, onCancel) => (
              <div className="space-y-3">
                <InlineText
                  label="Name"
                  value={(item.name as string) || ''}
                  onChange={(v) => { onChange('name', v); markChanged(); }}
                />
                <InlineText
                  label="Subname"
                  value={(item.subname as string) || ''}
                  onChange={(v) => { onChange('subname', v); markChanged(); }}
                />
                <InlineImage
                  label="Icon"
                  value={(item.imageUrl as string) || ''}
                  onChange={(v) => { onChange('imageUrl', v); markChanged(); }}
                  onUpload={handleImageUpload}
                />
                <div className="flex gap-2">
                  <Button onClick={onSave} size="sm" variant="blueBg">Done</Button>
                  <Button onClick={onCancel} variant="outline" size="sm">Cancel</Button>
                </div>
              </div>
            )}
          />
        </div>
      </InlineSectionCard>

      {/* Section 2: Rooftop Photo Gallery */}
      <InlineSectionCard
        title="Section 2: Rooftop Photo Gallery"
        description="Gallery images for rooftop section"
        icon={<ImageIcon className="w-5 h-5" />}
        onViewHistory={() => setHistoryDialog({ type: 'rooftop-gallery', id: rooftopId || 1 })}
      >
        <InlineText
          label="Gallery Subtitle"
          value={gallerySection.subtitle || ''}
          onChange={(v) => {
            setGallerySection((p) => ({ ...p, subtitle: v }));
            markChanged();
          }}
        />
        <InlineText
          label="Gallery Title"
          value={gallerySection.title || ''}
          onChange={(v) => {
            setGallerySection((p) => ({ ...p, title: v }));
            markChanged();
          }}
        />

        <div className="space-y-3 pt-4 border-t">
          <BatchImageUpload
            images={gallerySection.images.map(img => ({ 
              imageUrl: img.imageUrl, 
              id: img.id 
            }))}
            onChange={handleGalleryImagesChange}
            onDelete={async (imageId: number) => {
              try {
                await cmsApiService.deleteGalleryImageDetails(imageId);
                return true;
              } catch (error) {
                return false;
              }
            }}
            folder="rooftop"
            maxFiles={50}
            label="Upload Rooftop Gallery Images"
            description="Drag and drop multiple images here or click to browse"
          />
        </div>
      </InlineSectionCard>

      {/* Save All Button - Bottom Right */}
      <div className="flex justify-end pt-4 border-t mt-6">
        <Button 
          onClick={saveAllSections} 
          disabled={!hasUnsavedChanges || isSaving}
          className="min-w-[120px]"
          variant="blueBg"
        >
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? 'Saving...' : 'Save All'}
        </Button>
      </div>

      {historyDialog && (
        <VersionHistoryViewer
          entityType={historyDialog.type}
          entityId={historyDialog.id}
          isOpen={true}
          onClose={() => setHistoryDialog(null)}
        />
      )}
    </div>
  );
}
